<template>
  <div
    v-if="open"
    class="fixed z-[1260] rounded-2xl border border-[#ff009d49] bg-white/95 shadow-2xl p-4 sm:p-5 overflow-y-auto app-scroll"
    :style="menuStyle"
    @click.stop
  >
    <div class="flex items-center justify-between mb-4">
      <h5 class="text-sm font-semibold text-zinc-700">
        Video Settings
      </h5>
      <button
        type="button"
        class="rounded-md p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
        @click="$emit('close')"
      >
        <XMarkIcon class="w-4 h-4" />
      </button>
    </div>

    <div class="space-y-5">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Video Preview
        </p>
        <div class="rounded-lg overflow-hidden bg-zinc-100 aspect-video flex items-center justify-center">
          <img
            v-if="resolvedVideoPreviewUrl"
            :src="resolvedVideoPreviewUrl"
            alt="Video preview"
            class="w-full h-full object-cover"
            @error="onVideoPreviewImageError"
          >
          <p
            v-else
            class="text-xs text-zinc-500 px-3 text-center"
          >
            No video uploaded yet.
          </p>
        </div>
        <div
          v-if="showReadySpinner"
          class="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2"
        >
          <div class="flex items-center gap-2">
            <span class="inline-block h-4 w-4 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
            <p class="text-xs font-medium text-blue-700">
              {{ spinnerMessage }}
            </p>
          </div>
          <p
            v-if="showNavigateAwayMessage"
            class="mt-2 text-[11px] leading-tight font-semibold text-red-500"
          >
            You can navigate away, and continue working. Your video will display when completed. This May take awhile depending on file size
          </p>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Video Source
        </p>
        <button
          type="button"
          class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          :disabled="uploadActionsDisabled"
          @click="$emit('upload-request')"
        >
          {{ videoUploadButtonLabel }}
        </button>
        <p class="mt-2 text-xs text-zinc-500">
          You can also drag a video file directly onto the video placeholder in the lesson content.
        </p>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Thumbnail
        </p>
        <div class="rounded-lg overflow-hidden bg-zinc-100 aspect-video flex items-center justify-center mb-2">
          <img
            v-if="resolvedThumbnailPreviewUrl"
            :src="resolvedThumbnailPreviewUrl"
            alt="Thumbnail preview"
            class="w-full h-full object-cover"
            @error="onThumbnailPreviewImageError"
          >
          <p
            v-else
            class="text-xs text-zinc-500 px-3 text-center"
          >
            No thumbnail selected yet.
          </p>
        </div>
        <button
          type="button"
          class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          :disabled="uploadActionsDisabled"
          @click="$emit('thumbnail-upload-request')"
        >
          {{ thumbnailUploadButtonLabel }}
        </button>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Player Control Color
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
            :style="{ backgroundColor: normalizedControlColor }"
            @click="showControlColorPicker = !showControlColorPicker"
          />
          <input
            type="text"
            maxlength="7"
            class="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm text-zinc-700 uppercase focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
            :value="normalizedControlColor"
            @input="onControlColorTextInput"
            @blur="onControlColorTextBlur"
          >
          <button
            type="button"
            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
            @click="showControlColorPicker = !showControlColorPicker"
          >
            HEX
          </button>
        </div>
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <div
            v-if="showControlColorPicker"
            class="relative mt-3"
          >
            <ModernColorPicker
              v-model="controlColorModel"
              class="z-50 relative"
            />
          </div>
        </transition>
      </div>

      <div
        v-if="hasSource"
        class="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2"
      >
        <p class="text-sm font-medium text-zinc-700 [overflow-wrap:anywhere]">
          {{ normalizedFileName || 'Uploaded video' }}
        </p>
        <p
          v-if="normalizedFileMimeType"
          class="text-xs text-zinc-500 mt-0.5"
        >
          {{ normalizedFileMimeType }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import chatVideoProcessingPoster from '@/assets/media/images/chat-video-thumb.gif';
import ModernColorPicker from '@/Components/ModernColorPicker.vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  menuStyle: {
    type: Object,
    default: () => ({ top: '0px', left: '0px', width: '320px', height: '360px' }),
  },
  settings: {
    type: Object,
    default: () => ({
      videoFileId: null,
      videoUrl: '',
      videoFileName: '',
      videoFileMimeType: '',
      videoThumbnailFileId: null,
      videoThumbnailUrl: '',
      videoControlColor: '#2D8CFF',
    }),
  },
  videoPreviewUrl: {
    type: String,
    default: '',
  },
  thumbnailPreviewUrl: {
    type: String,
    default: '',
  },
  isUploading: {
    type: Boolean,
    default: false,
  },
  isProcessing: {
    type: Boolean,
    default: false,
  },
  isReady: {
    type: Boolean,
    default: false,
  },
  uploadProgress: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'upload-request', 'thumbnail-upload-request', 'update:settings']);

const normalizedFileName = computed(() => String(props.settings?.videoFileName || '').trim());
const normalizedFileMimeType = computed(() =>
  String(props.settings?.videoFileMimeType || '').trim().toLowerCase()
);
const normalizeColor = (value, fallback = '#2D8CFF') => {
  const raw = String(value || '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toUpperCase();
  return fallback;
};
const hasSource = computed(() =>
  Boolean(
    props.settings?.videoFileId ||
    String(props.settings?.videoUrl || '').trim()
  )
);
const normalizedControlColor = computed(() =>
  normalizeColor(props.settings?.videoControlColor, '#2D8CFF')
);
const normalizedProgress = computed(() => {
  const value = Number(props.uploadProgress || 0);
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
});
const videoPreviewImageErrored = ref(false);
const thumbnailPreviewImageErrored = ref(false);
const showControlColorPicker = ref(false);
watch(
  [() => props.videoPreviewUrl, () => props.thumbnailPreviewUrl],
  () => {
    videoPreviewImageErrored.value = false;
    thumbnailPreviewImageErrored.value = false;
  }
);
const shouldUseProcessingPreview = computed(() => {
  if (props.isUploading || props.isProcessing) return true;
  if (!hasSource.value) return false;
  const preview = String(props.videoPreviewUrl || '').trim();
  return !preview || videoPreviewImageErrored.value;
});
const resolvedVideoPreviewUrl = computed(() => {
  if (shouldUseProcessingPreview.value) return chatVideoProcessingPoster;
  return String(props.videoPreviewUrl || '').trim();
});
const resolvedThumbnailPreviewUrl = computed(() => {
  const thumb = String(props.thumbnailPreviewUrl || '').trim();
  if (thumb && !thumbnailPreviewImageErrored.value) return thumb;
  return '';
});
const showReadySpinner = computed(() => {
  if (props.isReady) return false;
  if (props.isUploading || props.isProcessing) return true;
  return hasSource.value;
});
const showNavigateAwayMessage = computed(() => {
  if (props.isReady) return false;
  return hasSource.value;
});
const uploadActionsDisabled = computed(() => props.isUploading || props.isProcessing);
const hasCustomThumbnail = computed(() =>
  Boolean(
    props.settings?.videoThumbnailFileId ||
    String(props.settings?.videoThumbnailUrl || '').trim()
  )
);
const videoUploadButtonLabel = computed(() =>
  hasSource.value ? 'Replace Video' : 'Upload Video'
);
const thumbnailUploadButtonLabel = computed(() =>
  hasCustomThumbnail.value ? 'Change Custom Thumbnail' : 'Upload Custom Thumbnail'
);
const spinnerMessage = computed(() => {
  if (props.isUploading) return `Uploading video... ${normalizedProgress.value}%`;
  if (props.isProcessing) return 'Video uploaded. Processing HLS stream...';
  if (hasSource.value) return 'Preparing video playback...';
  return '';
});
const onVideoPreviewImageError = () => {
  if (shouldUseProcessingPreview.value) return;
  videoPreviewImageErrored.value = true;
};
const onThumbnailPreviewImageError = () => {
  thumbnailPreviewImageErrored.value = true;
};
const controlColorModel = computed({
  get: () => normalizedControlColor.value,
  set: (value) => {
    emit('update:settings', { videoControlColor: normalizeColor(value, '#2D8CFF') });
  },
});
const onControlColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    emit('update:settings', { videoControlColor: withHash });
  }
};
const onControlColorTextBlur = (event) => {
  emit('update:settings', { videoControlColor: event?.target?.value });
};
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
}

.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent;
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
